import React, { useCallback, useMemo } from 'react';

import { renderErrorMessage } from '@lyearn/form/src/Form/components/InputFields/renderers';
import { ErrorPosition } from '@lyearn/form/src/Form/types';
import { TFunction, useTranslation } from '@lyearn/i18n';
import { css } from '@lyearn/style';
import { Box, Select, Stack, Typography } from '@lyearn/ui';
import { getSelectOptionLabel } from '@/components/Form/components/Select/getOptionLabel';
import { WeekOfMonthEnum } from '@/types/schema';

import { WeekOfMonth } from '../../../conts';
import { FrequencyComponentProps } from '../../../types';

const inputStyle = css`
  min-width: 0 !important;
  width: 0 !important;
  margin-left: 0;
  margin-right: 0;
`;

const CLASSES = {
  input: inputStyle,
};

const getValue = (item: { id: WeekOfMonthEnum; labelKey: string }, t: TFunction) => ({
  value: item.id,
  label: t(item.labelKey),
});

const getOptions = (items: { id: WeekOfMonthEnum; labelKey: string }[], t: TFunction) =>
  items.map((item) => getValue(item, t));

const VALUE_COUNT = 3;

const WeeksOfMonthInput = ({
  value,
  onChange,
  className,
  disabled,
  error,
  size,
}: FrequencyComponentProps & { error?: JSX.Element }) => {
  const { t } = useTranslation();
  const weeksOfMonth = value.weeksOfMonth ?? [WeekOfMonth[0].id];
  const onValueChange = useCallback(
    (value: WeekOfMonthEnum[]) => onChange({ weeksOfMonth: value }),
    [onChange],
  );

  const options = useMemo(() => getOptions(WeekOfMonth, t), [t]);

  const renderTags = React.useCallback(
    (values: typeof options) => {
      const typographyVariant = size === 'medium' ? 'body-short-02' : 'body-short-01';

      if (values?.length === 1) {
        return (
          <Typography color="text-text-paragraph" numberOfLines={1} variant={typographyVariant}>
            {values[0].label}
          </Typography>
        );
      }

      const labels = values?.reduce((acc, it, index) => {
        if (index > VALUE_COUNT - 1) return acc;
        if (it) acc.push(it.label);

        return acc;
      }, [] as string[]);

      return (
        <Box className="flex max-w-full items-center overflow-hidden">
          <Typography
            className="inline-block"
            color="text-text-paragraph"
            numberOfLines={1}
            variant={typographyVariant}>
            {labels?.join(', ')}
          </Typography>
          {values?.length <= VALUE_COUNT ? null : (
            <Typography
              className="inline-block ps-4 pe-8"
              color="text-text-secondary"
              variant={typographyVariant}>
              {`+${values?.length - VALUE_COUNT}`}
            </Typography>
          )}
        </Box>
      );
    },
    [size],
  );

  return (
    <Stack className={className ?? 'w-1/2'} direction="col" gapY={8}>
      <Select
        disableClearable
        multiple
        className="w-full"
        classes={weeksOfMonth.length ? CLASSES : undefined}
        disabled={disabled}
        error={Boolean(error)}
        getOptionLabel={getSelectOptionLabel}
        options={options}
        placeholder={t('Common:WeekOfMonth.Placeholder')}
        renderTags={renderTags}
        size={size ?? 'large'}
        value={weeksOfMonth}
        onChange={onValueChange}
      />
      {error
        ? renderErrorMessage({ errorMessage: error, errorPosition: ErrorPosition.Custom })
        : null}
    </Stack>
  );
};

export default WeeksOfMonthInput;
